/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Knowledge › Company knowledge, rebuilt on the kit (overhaul plan step 3d): the same /v1/admin/knowledge calls and the
 * same two-step removal as before. The eight cards that were one scroll are five views in the page header (Projects,
 * Folders & exclusions, Pools, Sources, Party report, each its own `?view=`); the project form and the folders form open
 * in a side drawer next to their button instead of below the whole table; the removals awaiting approval are a callout
 * above the projects; status is chips in the header; lifecycle reads from the kit's one tone map.
 *
 * IT admins keep the project list (one row per project number: its folders, the direct client, the end user, who may
 * read it, its lifecycle), the company-wide folders and the exclusions, the pools, and see what each partition holds.
 * Removing a project's data takes two separate steps, a request and then an approval, and only then the partition is
 * purged; the same admin may do both (the owner's ruling of 2026-10-10: Exante360 has one IT admin), and the audit
 * record says when that happened. The per-party report lists everything held for a client or an end user. The
 * retrieval flag itself is set under Governance › Feature flags (`company-knowledge`, percent only). Auditors see
 * everything and can change nothing (the routes refuse).
 */
import { useCallback, useEffect, useState } from 'react';
import { FileSearch, FolderTree, Library, MoreHorizontal, Pencil, Plus, RefreshCw, ShieldCheck, Trash2, Users } from 'lucide-react';
import { api } from '../lib/api';
import {
  SETTABLE_LIFECYCLES,
  clockLabel,
  describeKnowledgeError,
  folderLines,
  foldersFromLines,
  formatBytes,
  isLibraryAbsent,
  parseLines,
  parseList,
  projectsPutBody,
  scopeTextFor,
  toProjectInput,
  type DocumentRow,
  type KnowledgeStatus,
  type Lifecycle,
  type PoolRow,
  type PoolsView,
  type ProjectInput,
  type ProjectView,
  type ProjectsView,
  type RemovalRow,
  type ReportProject,
  type ReportView,
  type SourcesPage,
} from '../lib/knowledge';
import { usePlace } from '../shell/router';
import {
  Button, Callout, Checkbox, Drawer, EmptyState, Field, Grid, Input, LIFECYCLE_CHIPS, MenuButton, PageHeader, Panel, Select, Skeleton, SpecChip, Stack, StatusChip, Table, Tag, Textarea,
  type Column, type MenuItem,
} from '../ui';
import { count, plural } from '../ui/format';

interface ProjectForm {
  id: string;
  folderPaths: string;
  directClient: string;
  endUser: string;
  pools: string;
  namedReaders: string;
  restricted: boolean;
  lifecycle: Lifecycle;
  notes: string;
}

interface PoolForm {
  id: string;
  name: string;
  emails: string;
  entraGroupId: string;
}

const EMPTY_FORM: ProjectForm = { id: '', folderPaths: '', directClient: '', endUser: '', pools: '', namedReaders: '', restricted: false, lifecycle: 'active', notes: '' };

function formOf(row: ProjectView): ProjectForm {
  return {
    id: row.id,
    folderPaths: row.folderPaths.join('\n'),
    directClient: row.directClient,
    endUser: row.endUser ?? '',
    pools: row.pools.join(', '),
    namedReaders: row.namedReaders.join(', '),
    restricted: row.restricted,
    lifecycle: row.lifecycle === 'removed' ? 'active' : row.lifecycle,
    notes: row.notes,
  };
}

function inputOf(form: ProjectForm): ProjectInput {
  const notes = form.notes.trim();
  return {
    id: form.id.trim().toLowerCase(),
    folderPaths: parseLines(form.folderPaths),
    directClient: form.directClient.trim(),
    endUser: form.endUser.trim() || null,
    pools: parseList(form.pools),
    namedReaders: parseList(form.namedReaders),
    restricted: form.restricted,
    lifecycle: form.lifecycle,
    ...(notes ? { notes } : {}),
  };
}

function poolFormOf(pool: PoolRow): PoolForm {
  return { id: pool.id, name: pool.name, emails: pool.emails.join('\n'), entraGroupId: pool.entraGroupId ?? '' };
}

function poolOf(form: PoolForm): PoolRow {
  return { id: form.id.trim().toLowerCase(), name: form.name.trim(), emails: parseList(form.emails), entraGroupId: form.entraGroupId.trim() || null };
}

function lifecycleWords(lifecycle: Lifecycle): string {
  return lifecycle === 'removal_requested' ? 'removal requested' : lifecycle;
}

function day(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

/** The page's views, each its own `?view=` so it can be linked to. */
type View = 'projects' | 'folders' | 'pools' | 'sources' | 'report';
const VIEWS: readonly View[] = ['projects', 'folders', 'pools', 'sources', 'report'];
const isView = (value: string | null): value is View => (VIEWS as readonly string[]).includes(value ?? '');

export function Knowledge() {
  const place = usePlace();
  const [status, setStatus] = useState<KnowledgeStatus | null>(null);
  const [list, setList] = useState<ProjectsView | null>(null);
  const [pools, setPools] = useState<PoolsView | null>(null);
  const [removals, setRemovals] = useState<RemovalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [absent, setAbsent] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const [editId, setEditId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<ProjectForm>(EMPTY_FORM);

  const [foldersOpen, setFoldersOpen] = useState(false);
  const [internalText, setInternalText] = useState('');
  const [noneText, setNoneText] = useState('');
  const [exclusionsText, setExclusionsText] = useState('');

  const [poolForms, setPoolForms] = useState<PoolForm[] | null>(null);

  const [sourceScope, setSourceScope] = useState('internal');
  const [sources, setSources] = useState<SourcesPage | null>(null);
  const [sourcesBusy, setSourcesBusy] = useState(false);

  const [party, setParty] = useState('');
  const [report, setReport] = useState<ReportView | null>(null);

  const asked = new URLSearchParams(place.search).get('view');
  const view: View = isView(asked) ? asked : 'projects';
  const hrefFor = (id: View) => {
    const next = new URLSearchParams(place.search);
    next.set('view', id);
    return `/knowledge?${next.toString()}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, l, p, r] = await Promise.all([api.knowledgeStatus(), api.knowledgeProjects(), api.knowledgePools(), api.knowledgeRemovals()]);
      setStatus(s);
      setList(l);
      setPools(p);
      setRemovals(r.removals);
      setAbsent(false);
    } catch (e) {
      if (isLibraryAbsent(e)) setAbsent(true);
      else setError(describeKnowledgeError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Run one change: the message on success, plain words on a refusal, and the lists reloaded either way (a stale revision is the common case). */
  const run = async (key: string, action: () => Promise<string>) => {
    setBusy(key);
    setMsg('');
    setError('');
    try {
      setMsg(await action());
    } catch (e) {
      setError(describeKnowledgeError(e));
    } finally {
      setBusy(null);
      await load();
    }
  };

  const startEdit = (row: ProjectView) => {
    setAdding(false);
    setEditId(row.id);
    setForm(formOf(row));
  };

  const startAdd = () => {
    setEditId(null);
    setAdding(true);
    setForm(EMPTY_FORM);
  };

  const cancelForm = () => {
    setEditId(null);
    setAdding(false);
    setForm(EMPTY_FORM);
  };

  const saveForm = () => {
    if (!list) return;
    const input = inputOf(form);
    if (adding) {
      if (!input.id) {
        setError('A project needs an id: the project number the folder names carry (24.0123), or a folder key (aws-iad-12).');
        return;
      }
      if (list.projects.some((row) => row.id === input.id)) {
        setError(`Project ${input.id} is already on the list. Edit that row instead.`);
        return;
      }
      void run('add', async () => {
        await api.knowledgePutProjects(projectsPutBody(list, [...list.projects.map(toProjectInput), input]));
        cancelForm();
        return `Project ${input.id} added. Nothing is imported until the crawler posts its documents.`;
      });
      return;
    }
    if (!editId) return;
    const { id: _id, ...patch } = input;
    void run(`edit:${editId}`, async () => {
      const result = await api.knowledgePatchProject(editId, { ...patch, notes: input.notes ?? '', expectedRevision: list.revision });
      cancelForm();
      return `Project ${result.project.id} saved (list revision ${result.revision}).`;
    });
  };

  const dropProject = (row: ProjectView) => {
    if (!list) return;
    const words = row.lifecycle === 'removed'
      ? `Take project ${row.id} off the list? Its data was already removed.`
      : `Take project ${row.id} off the list? This is refused while the project still holds documents; to delete its documents, request a removal instead.`;
    if (!window.confirm(words)) return;
    void run(`drop:${row.id}`, async () => {
      await api.knowledgePutProjects(projectsPutBody(list, list.projects.filter((candidate) => candidate.id !== row.id).map(toProjectInput)));
      return `Project ${row.id} is off the list.`;
    });
  };

  const requestRemoval = (row: ProjectView) => {
    const reason = window.prompt(`Why is project ${row.id} being removed from the library? The reason goes on the audit record; the deletion happens only when the request is approved below, as a separate step.`, '');
    if (reason === null) return;
    if (!reason.trim()) {
      setError('A removal needs a reason.');
      return;
    }
    void run(`remove:${row.id}`, async () => {
      const result = await api.knowledgeRequestRemoval(row.id, reason.trim());
      return `Removal of project ${row.id} requested (${result.removalId}). The project takes no more imports; approve the request below to delete its documents.`;
    });
  };

  const decideRemoval = (removal: RemovalRow, decision: 'approve' | 'reject') => {
    if (decision === 'approve' && !window.confirm(`Approve the removal of project ${removal.projectId}? Every document, memory line and cache of it is deleted. This cannot be undone.`)) return;
    void run(`decide:${removal.id}`, async () => {
      const result = await api.knowledgeDecideRemoval(removal.id, decision);
      if (decision === 'reject') return `Removal of project ${removal.projectId} rejected.`;
      const counts = result.counts ?? {};
      return `Project ${removal.projectId} removed: ${counts.documents ?? 0} documents, ${counts.sections ?? 0} sections, ${counts.memoryLines ?? 0} memory lines. The audit record carries the counts.`;
    });
  };

  const openFolders = () => {
    if (!list) return;
    setInternalText(folderLines(list.folders, 'internal'));
    setNoneText(folderLines(list.folders, 'none'));
    setExclusionsText(list.exclusions.join('\n'));
    setFoldersOpen(true);
  };

  const saveFolders = () => {
    if (!list) return;
    void run('folders', async () => {
      const folders = [...foldersFromLines(internalText, 'internal'), ...foldersFromLines(noneText, 'none')];
      await api.knowledgePutProjects(projectsPutBody(list, list.projects.map(toProjectInput), folders, parseLines(exclusionsText)));
      setFoldersOpen(false);
      return 'Folders and exclusions saved.';
    });
  };

  const savePools = () => {
    if (!pools || !poolForms) return;
    void run('pools', async () => {
      await api.knowledgePutPools({ pools: poolForms.map(poolOf), expectedRevision: pools.revision });
      setPoolForms(null);
      return 'Pools saved.';
    });
  };

  const loadSources = async (scopeSelection: string, page: number) => {
    setSourcesBusy(true);
    setError('');
    try {
      setSources(await api.knowledgeSources(scopeTextFor(scopeSelection), page, 100));
    } catch (e) {
      setSources(null);
      setError(describeKnowledgeError(e));
    } finally {
      setSourcesBusy(false);
    }
  };

  const deleteSource = (path: string) => {
    if (!sources || !window.confirm(`Delete this document from the library?\n${path}\nThe crawler posts it again on its next pass unless its folder is excluded.`)) return;
    void run(`source:${path}`, async () => {
      const result = await api.knowledgeDeleteSource(scopeTextFor(sourceScope), path);
      await loadSources(sourceScope, sources.page);
      return result.status === 'deleted' ? 'Document deleted.' : 'That document was already gone.';
    });
  };

  const loadReport = async () => {
    const name = party.trim();
    if (!name) return;
    setBusy('report');
    setError('');
    try {
      setReport(await api.knowledgeReport(name));
    } catch (e) {
      setReport(null);
      setError(describeKnowledgeError(e));
    } finally {
      setBusy(null);
    }
  };

  const locked = busy !== null;

  const projectColumns: Array<Column<ProjectView>> = [
    {
      key: 'project', header: 'Project',
      cell: (row) => (
        <span className="ui-cell-stack">
          <Tag>{row.id}</Tag>
          <span className="ui-cell-sub">
            {row.folderPaths.length ? plural(row.folderPaths.length, 'folder') : 'Any path with this id'}
            {row.notes && <span title={row.notes}> · {row.notes.length > 60 ? `${row.notes.slice(0, 59)}…` : row.notes}</span>}
          </span>
        </span>
      ),
    },
    { key: 'client', header: 'Direct client', cell: (row) => row.directClient },
    { key: 'endUser', header: 'End user', cell: (row) => (row.endUserUnknown ? <StatusChip tone="warn">End user not filled in</StatusChip> : row.endUser) },
    {
      key: 'readers', header: 'Readers',
      cell: (row) => (
        <span className="ui-cell-stack">
          <span className="ui-chips">
            {row.restricted
              ? <StatusChip tone="info" plain title="Only the named readers; pools and principals do not read it">Restricted</StatusChip>
              : row.pools.length > 0 ? row.pools.map((pool) => <Tag key={pool}>{pool}</Tag>) : <StatusChip tone="warn">No pool</StatusChip>}
          </span>
          {row.namedReaders.length > 0 && <span className="ui-cell-sub">{plural(row.namedReaders.length, 'named reader')}</span>}
        </span>
      ),
    },
    {
      key: 'lifecycle', header: 'Lifecycle',
      cell: (row) => (
        <span className="ui-cell-stack">
          <span><SpecChip spec={LIFECYCLE_CHIPS[row.lifecycle]} /></span>
          {row.clock && <span className={row.clock.overdue ? 'ui-cell-sub ui-text-bad' : 'ui-cell-sub'}>{clockLabel(row.clock)}</span>}
          {row.lifecycle === 'removed' && row.dates.removedAt && <span className="ui-cell-sub">Removed {day(row.dates.removedAt)}</span>}
        </span>
      ),
    },
    {
      key: 'actions', header: <span className="ui-sr">Actions</span>, align: 'end', width: '56px',
      cell: (row) => {
        const items: MenuItem[] = [{ id: 'edit', label: 'Edit', icon: Pencil, onSelect: () => startEdit(row) }];
        if (row.lifecycle !== 'removed' && row.lifecycle !== 'removal_requested') {
          items.push({ id: 'remove', label: 'Request removal', icon: ShieldCheck, onSelect: () => requestRemoval(row) });
        }
        items.push({ id: 'drop', label: 'Take off the list', icon: Trash2, onSelect: () => dropProject(row) });
        return <MenuButton label={`Actions for project ${row.id}`} icon={MoreHorizontal} items={items} />;
      },
    },
  ];

  const poolColumns: Array<Column<PoolRow>> = [
    { key: 'pool', header: 'Pool', cell: (pool) => <Tag>{pool.id}</Tag> },
    { key: 'name', header: 'Name', cell: (pool) => pool.name },
    { key: 'members', header: 'Members', muted: true, cell: (pool) => <span title={pool.emails.join('\n')}>{plural(pool.emails.length, 'e-mail')}</span> },
    { key: 'group', header: 'Entra group', muted: true, mono: true, cell: (pool) => pool.entraGroupId ?? '—' },
  ];

  const documentColumns: Array<Column<DocumentRow>> = [
    { key: 'path', header: 'Path', cell: (doc) => <span className="ui-break is-mono">{doc.path}</span> },
    { key: 'title', header: 'Title', cell: (doc) => doc.title },
    { key: 'modified', header: 'Modified', muted: true, hideOnPhone: true, cell: (doc) => day(doc.modifiedAt) },
    { key: 'size', header: 'Size', numeric: true, muted: true, cell: (doc) => formatBytes(doc.sizeBytes) },
    { key: 'sections', header: 'Sections', numeric: true, muted: true, hideOnPhone: true, cell: (doc) => count(doc.sectionCount) },
    { key: 'dlp', header: 'DLP', numeric: true, muted: true, hideOnPhone: true, cell: (doc) => <span title="Redactions made at import, and sections dropped whole">{doc.dlpHits}{doc.droppedSections ? ` (${doc.droppedSections} dropped)` : ''}</span> },
    { key: 'imported', header: 'Imported', muted: true, hideOnPhone: true, cell: (doc) => day(doc.importedAt) },
    {
      key: 'actions', header: <span className="ui-sr">Actions</span>, align: 'end', width: '56px',
      cell: (doc) => <Button variant="ghost" size="sm" icon={Trash2} disabled={locked} aria-label={`Delete ${doc.path}`} title="Delete this document from the library" onClick={() => deleteSource(doc.path)} />,
    },
  ];

  const reportColumns: Array<Column<ReportProject>> = [
    { key: 'project', header: 'Project', cell: (project) => <span className="ui-cell-stack"><Tag>{project.projectId}</Tag><span className="ui-cell-sub">{project.directClient}{project.endUser ? ` for ${project.endUser}` : ''}</span></span> },
    { key: 'role', header: 'Role', cell: (project) => (project.role === 'both' ? 'direct client and end user' : project.role === 'endUser' ? 'end user' : 'direct client') },
    { key: 'lifecycle', header: 'Lifecycle', cell: (project) => <SpecChip spec={LIFECYCLE_CHIPS[project.lifecycle]} /> },
    { key: 'clock', header: 'Clock', muted: true, cell: (project) => clockLabel(project.clock) || '—' },
    { key: 'documents', header: 'Documents', numeric: true, cell: (project) => <span title={project.documents.join('\n')}>{count(project.documentsTotal)}{project.documentsTruncated && <span className="ui-quiet"> (first {project.documents.length} listed)</span>}</span> },
    { key: 'sections', header: 'Sections', numeric: true, muted: true, cell: (project) => count(project.counts.sections) },
    { key: 'memory', header: 'Memory lines', numeric: true, muted: true, cell: (project) => count(project.counts.memoryLines) },
    { key: 'partition', header: 'Partition', muted: true, cell: (project) => `${project.partition.state}${project.partition.removedAt ? ` ${day(project.partition.removedAt)}` : ''}` },
  ];

  const retrievalChip = status
    ? status.flag.enabled
      ? <StatusChip tone="ok">Retrieval on for {status.flag.percent ?? 100}% of people</StatusChip>
      : <StatusChip tone="neutral">{status.flag.stored ? 'Retrieval off' : 'Retrieval not switched on yet'}</StatusChip>
    : null;
  const tabs = [
    { id: 'projects', label: 'Projects', href: hrefFor('projects'), count: list?.projects.length },
    { id: 'folders', label: 'Folders & exclusions', href: hrefFor('folders') },
    { id: 'pools', label: 'Pools', href: hrefFor('pools'), count: pools?.pools.length },
    { id: 'sources', label: 'Sources', href: hrefFor('sources') },
    { id: 'report', label: 'Party report', href: hrefFor('report') },
  ];

  return (
    <>
      <PageHeader
        meta={status ? (
          <>
            {retrievalChip}
            <StatusChip tone={status.storage === 'durable' ? 'info' : 'warn'}>{status.storage === 'durable' ? 'Durable storage' : 'Local storage'}</StatusChip>
            {status.orphans === null && <StatusChip tone="warn">Partitions could not be listed</StatusChip>}
          </>
        ) : loading ? <Skeleton width="220px" /> : null}
        actions={(
          <>
            <Button icon={RefreshCw} disabled={loading} onClick={() => void load()} aria-label="Reload" className="is-collapse-phone">Reload</Button>
            {list && !absent && <Button variant="primary" icon={Plus} disabled={locked || adding} onClick={startAdd}>Add project</Button>}
          </>
        )}
        tabs={absent ? undefined : tabs}
        activeTab={view}
      />
      <div className="ui-page">
        <Stack>
          {msg && <Callout tone="ok" role="status">{msg}</Callout>}
          {error && <Callout tone="bad" role="alert" title="The service refused">{error}</Callout>}

          {absent && !loading && (
            <EmptyState icon={Library} title="The company knowledge library is not on this service yet">
              It ships with the next release. Nothing to manage here until then.
            </EmptyState>
          )}

          {!absent && view === 'projects' && (
            <>
              {status?.orphans && status.orphans.length > 0 && (
                <Callout tone="warn" title={`${plural(status.orphans.length, 'partition')} hold documents for a project that is not on the list`}>
                  {status.orphans.map((orphan) => orphan.projectId).join(', ')}. Add the project back to the list (its documents come back under it) or request its removal after adding it.
                </Callout>
              )}
              {removals.map((removal) => (
                <Callout
                  key={removal.id}
                  tone="warn"
                  icon={ShieldCheck}
                  title={`Removal of project ${removal.projectId} is waiting for a second IT admin`}
                  action={(
                    <>
                      <Button size="sm" disabled={locked} onClick={() => decideRemoval(removal, 'reject')}>Reject</Button>
                      <Button variant="danger" size="sm" icon={Trash2} disabled={locked} onClick={() => decideRemoval(removal, 'approve')}>Approve and delete</Button>
                    </>
                  )}
                >
                  “{removal.reason}” · requested by {removal.requestedBy} on {new Date(removal.at).toISOString().slice(0, 10)}. Approving deletes every document of the project;
                  the request and the approval are two separate steps, each on the audit record. A request waits seven days, then lapses.
                </Callout>
              ))}
              <Panel
                flush
                title="Projects"
                description={list ? `List revision ${list.revision}` : 'The project list the crawler fills the library against'}
                footer={(
                  <span>
                    A row leaves the list only when its partition is empty or was purged by a removal. Setting a project <em>completed</em> or requesting its removal starts the
                    30-day deletion clock the strategy promises clients. Retrieval itself is switched on under Governance › Feature flags (<code>company-knowledge</code>, percent only).
                  </span>
                )}
              >
                <Table
                  label="Projects"
                  columns={projectColumns}
                  rows={list?.projects ?? []}
                  rowKey={(row) => row.id}
                  selectedKey={editId}
                  loading={loading}
                  skeletonRows={5}
                  className="ui-table--tall"
                  empty={<EmptyState compact icon={FolderTree} title="No projects yet" action={<Button variant="primary" size="sm" icon={Plus} onClick={startAdd}>Add project</Button>}>Add the reviewed seed list (RUNBOOK-company-knowledge.md, step 3).</EmptyState>}
                  phone={{
                    title: (row) => `${row.id} · ${row.directClient}`,
                    meta: (row) => row.endUserUnknown ? 'End user not filled in' : (row.endUser ?? ''),
                    value: (row) => <SpecChip spec={LIFECYCLE_CHIPS[row.lifecycle]} />,
                    sub: (row) => <Button variant="ghost" size="sm" icon={Pencil} onClick={() => startEdit(row)}>Edit</Button>,
                  }}
                />
              </Panel>
            </>
          )}

          {!absent && view === 'folders' && list && (
            <Panel
              title="Company-wide folders and exclusions"
              description="Internal folders are read by every verified person; none folders are stored and reported, never read; an exclusion wins over every project and folder row"
              actions={<Button size="sm" icon={Pencil} disabled={locked} onClick={openFolders}>Edit</Button>}
            >
              <Grid cols={2}>
                <div className="ui-form">
                  <Field label={`Internal (${count(list.folders.filter((f) => f.scope === 'internal').length)})`}>
                    <pre className="ui-pre">{folderLines(list.folders, 'internal') || '—'}</pre>
                  </Field>
                </div>
                <div className="ui-form">
                  <Field label={`None (${count(list.folders.filter((f) => f.scope === 'none').length)})`}>
                    <pre className="ui-pre">{folderLines(list.folders, 'none') || '—'}</pre>
                  </Field>
                  <Field label={`Excluded, never imported (${count(list.exclusions.length)})`}>
                    <pre className="ui-pre">{list.exclusions.join('\n') || '—'}</pre>
                  </Field>
                </div>
              </Grid>
            </Panel>
          )}

          {!absent && view === 'pools' && pools && (
            <Panel
              flush={!poolForms}
              title="Pools"
              description={`Revision ${pools.revision}`}
              actions={!poolForms ? <Button size="sm" icon={Pencil} disabled={locked} onClick={() => setPoolForms(pools.pools.map(poolFormOf))}>Edit</Button> : null}
              footer={(
                <span>
                  A pool is a group of people who read the projects that name it. <code>{pools.principalsPool}</code> reads every project that is not restricted. E-mails are the
                  interim membership; the Entra group id takes over when The Bridge&apos;s group read exists (the-bridge#760).
                </span>
              )}
            >
              {!poolForms ? (
                <Table stacked
                  label="Pools"
                  columns={poolColumns}
                  rows={pools.pools}
                  rowKey={(pool) => pool.id}
                  stickyHeader={false}
                  empty={<EmptyState compact icon={Users} title="No pools yet">The runbook&apos;s turn-on order starts with <code>production</code>, <code>pms</code> and <code>principals</code>.</EmptyState>}
                />
              ) : (
                <div className="ui-form">
                  {poolForms.map((pool, index) => (
                    <div key={index} className={index ? 'ui-pool-form is-divided' : 'ui-pool-form'}>
                      <Grid cols={2}>
                        <div className="ui-form">
                          <Field label="Pool id (a-z, 0-9, hyphens)">
                            <Input value={pool.id} onChange={(e) => setPoolForms(poolForms.map((p, i) => (i === index ? { ...p, id: e.target.value } : p)))} placeholder="production" />
                          </Field>
                          <Field label="Name">
                            <Input value={pool.name} onChange={(e) => setPoolForms(poolForms.map((p, i) => (i === index ? { ...p, name: e.target.value } : p)))} placeholder="Production" />
                          </Field>
                          <Field label="Entra group object id (optional; not read yet)">
                            <Input value={pool.entraGroupId} onChange={(e) => setPoolForms(poolForms.map((p, i) => (i === index ? { ...p, entraGroupId: e.target.value } : p)))} />
                          </Field>
                          <div className="ui-actions">
                            <Button variant="danger" size="sm" icon={Trash2} disabled={locked} onClick={() => setPoolForms(poolForms.filter((_, i) => i !== index))}>Remove pool</Button>
                          </div>
                        </div>
                        <Field label="Members, one e-mail per line">
                          <Textarea rows={8} value={pool.emails} onChange={(e) => setPoolForms(poolForms.map((p, i) => (i === index ? { ...p, emails: e.target.value } : p)))} />
                        </Field>
                      </Grid>
                    </div>
                  ))}
                  <div className="ui-actions">
                    <Button icon={Plus} disabled={locked} onClick={() => setPoolForms([...poolForms, { id: '', name: '', emails: '', entraGroupId: '' }])}>Add pool</Button>
                    <Button variant="primary" disabled={locked} onClick={savePools}>Save pools</Button>
                    <Button variant="ghost" disabled={locked} onClick={() => setPoolForms(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </Panel>
          )}

          {!absent && view === 'sources' && list && (
            <Panel
              flush
              title={<span className="ui-title-icon"><FileSearch size={16} strokeWidth={1.75} aria-hidden="true" />Sources</span>}
              description="What a partition holds: metadata only, never the text"
            >
              <div className="ui-panel-pad ui-panel-pad--divided">
                <div className="ui-actions">
                  <Select aria-label="Partition" value={sourceScope} onChange={(e) => setSourceScope(e.target.value)} className="ui-w-lg">
                    <option value="internal">internal</option>
                    <option value="none">none</option>
                    {list.projects.map((row) => (
                      <option key={row.id} value={row.id}>
                        project {row.id} ({row.directClient}{row.endUser ? ` for ${row.endUser}` : ''})
                      </option>
                    ))}
                  </Select>
                  <Button variant="primary" busy={sourcesBusy} onClick={() => void loadSources(sourceScope, 1)}>Show</Button>
                </div>
              </div>
              {sources ? (
                <>
                  <div className="ui-panel-pad ui-panel-pad--divided ui-pager">
                    <span className="ui-cell-sub">
                      Partition <code>{sources.partition}</code>: {plural(sources.total, 'document')}; page {sources.page} of {sources.pages}.
                    </span>
                    {sources.pages > 1 && (
                      <span className="ui-row-actions">
                        <Button size="sm" disabled={sources.page <= 1 || sourcesBusy} onClick={() => void loadSources(sourceScope, sources.page - 1)}>Previous</Button>
                        <Button size="sm" disabled={sources.page >= sources.pages || sourcesBusy} onClick={() => void loadSources(sourceScope, sources.page + 1)}>Next</Button>
                      </span>
                    )}
                  </div>
                  <Table stacked
                    label="Documents"
                    columns={documentColumns}
                    rows={sources.documents}
                    rowKey={(doc) => doc.documentId}
                    stickyHeader={false}
                    className="ui-table--tall"
                    empty={<EmptyState compact icon={FileSearch} title="Nothing in this partition">The crawler has posted no documents for it yet.</EmptyState>}
                  />
                </>
              ) : (
                <EmptyState compact icon={FileSearch} title="Pick a partition and press Show">Each document&apos;s path, size, sections and DLP hits are listed; the text never leaves the library.</EmptyState>
              )}
            </Panel>
          )}

          {!absent && view === 'report' && list && (
            <Panel
              flush
              title={<span className="ui-title-icon"><ShieldCheck size={16} strokeWidth={1.75} aria-hidden="true" />What we hold for a client or an end user</span>}
              description="Every project that names the party, with its counts and its deletion clock"
            >
              <div className="ui-panel-pad ui-panel-pad--divided">
                <div className="ui-actions">
                  <Input
                    className="ui-grow"
                    aria-label="Party"
                    value={party}
                    onChange={(e) => setParty(e.target.value)}
                    placeholder="Exact name of a direct client or an end user"
                    onKeyDown={(e) => { if (e.key === 'Enter') void loadReport(); }}
                  />
                  <Button variant="primary" busy={busy === 'report'} disabled={locked || !party.trim()} onClick={() => void loadReport()}>Report</Button>
                </div>
              </div>
              {report ? (
                <>
                  <div className="ui-panel-pad ui-panel-pad--divided">
                    <span className="ui-cell-sub">
                      {plural(report.projects.length, 'project')} name {report.party}, as of {report.generatedAt.slice(0, 16).replace('T', ' ')} UTC.
                      {report.survives.length === 0 && ' Nothing is kept past a removal.'}
                    </span>
                  </div>
                  <Table stacked
                    label="Party report"
                    columns={reportColumns}
                    rows={report.projects}
                    rowKey={(project) => project.projectId}
                    stickyHeader={false}
                    className="ui-table--tall"
                    empty={<EmptyState compact icon={ShieldCheck} title="No project names this party">Nothing is held for them.</EmptyState>}
                  />
                  {report.notCovered.length > 0 && (
                    <div className="ui-panel-pad ui-panel-pad--top">
                      <ul className="ui-list ui-cell-sub">{report.notCovered.map((line) => <li key={line}>{line}</li>)}</ul>
                    </div>
                  )}
                </>
              ) : (
                <EmptyState compact icon={ShieldCheck} title="Type a party's exact name and press Report">The report lists every project held for a direct client or an end user.</EmptyState>
              )}
            </Panel>
          )}
        </Stack>
      </div>

      {/* The project form: Add project, or Edit on a row. */}
      <Drawer
        open={(adding || editId !== null) && list !== null}
        onClose={cancelForm}
        title={adding ? 'Add a project' : `Edit project ${editId}`}
        description="One row per project number: its folders, the direct client, the end user, who may read it, its lifecycle"
        footer={(
          <>
            <Button variant="ghost" disabled={locked} onClick={cancelForm}>Cancel</Button>
            <Button variant="primary" disabled={locked} onClick={saveForm}>{adding ? 'Add project' : 'Save'}</Button>
          </>
        )}
      >
        <div className="ui-form">
          <Grid cols={2}>
            <Field label="Project id" help="The project number, or a folder key">
              <Input value={form.id} disabled={!adding} onChange={(e) => setForm({ ...form, id: e.target.value })} placeholder="24.0123" />
            </Field>
            <Field label="Lifecycle">
              <Select value={form.lifecycle} onChange={(e) => setForm({ ...form, lifecycle: e.target.value as Lifecycle })}>
                {SETTABLE_LIFECYCLES.map((lifecycle) => <option key={lifecycle} value={lifecycle}>{lifecycleWords(lifecycle)}</option>)}
              </Select>
            </Field>
          </Grid>
          <Field label="Direct client" help="Who signed the contract with us">
            <Input value={form.directClient} onChange={(e) => setForm({ ...form, directClient: e.target.value })} placeholder="Ashford Architects" />
          </Field>
          <Field label="End user" help="Whose project it really is; blank until known, and then flagged">
            <Input value={form.endUser} onChange={(e) => setForm({ ...form, endUser: e.target.value })} placeholder="Tidewater Health" />
          </Field>
          <Field label="Pools that may read it" help="Comma-separated pool ids">
            <Input value={form.pools} onChange={(e) => setForm({ ...form, pools: e.target.value })} placeholder="production, pms" />
          </Field>
          <Field label="Named readers" help="E-mails, comma-separated">
            <Input value={form.namedReaders} onChange={(e) => setForm({ ...form, namedReaders: e.target.value })} placeholder="name@exante360.com" />
          </Field>
          <Field label="Folder paths, one per line" help="As the crawler sees them; blank means any path the crawler stamps with this id">
            <Textarea rows={3} className="is-mono" value={form.folderPaths} onChange={(e) => setForm({ ...form, folderPaths: e.target.value })} placeholder="S:/PROJECTS/Ashford Architects/24.0123 Tidewater Health" />
          </Field>
          <Field label="Notes" help="For admins; never read on a turn">
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <Checkbox checked={form.restricted} onChange={(e) => setForm({ ...form, restricted: e.target.checked })} label="Restricted: only the named readers may read it (pools and principals do not)" />
        </div>
      </Drawer>

      {/* The folders form. */}
      <Drawer
        open={foldersOpen && list !== null}
        onClose={() => setFoldersOpen(false)}
        title="Edit folders and exclusions"
        description="One path per line"
        footer={(
          <>
            <Button variant="ghost" disabled={locked} onClick={() => setFoldersOpen(false)}>Cancel</Button>
            <Button variant="primary" disabled={locked} onClick={saveFolders}>Save</Button>
          </>
        )}
      >
        <div className="ui-form">
          <Field label="Internal folders, one per line" help="Company knowledge, read by every verified person">
            <Textarea rows={6} className="is-mono" value={internalText} onChange={(e) => setInternalText(e.target.value)} />
          </Field>
          <Field label="None folders, one per line" help="Stored and reported, never read on a turn">
            <Textarea rows={6} className="is-mono" value={noneText} onChange={(e) => setNoneText(e.target.value)} />
          </Field>
          <Field label="Excluded folders, one per line" help="An exclusion wins over every project and folder row">
            <Textarea rows={4} className="is-mono" value={exclusionsText} onChange={(e) => setExclusionsText(e.target.value)} />
          </Field>
        </div>
      </Drawer>
    </>
  );
}
